import { useNavigate } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';

/**
 * Global keyboard shortcuts hook
 * Provides application-wide keyboard shortcuts for navigation and actions
 */
export const useKeyboardShortcuts = (options: {
  onSearchOpen?: () => void;
  onModalClose?: () => void;
  disabled?: boolean;
} = {}) => {
  const navigate = useNavigate();
  const { onSearchOpen, onModalClose, disabled = false } = options;

  // Cmd/Ctrl + K: Open global search
  useHotkeys(
    'mod+k',
    (e) => {
      e.preventDefault();
      if (onSearchOpen) {
        onSearchOpen();
      }
    },
    { enabled: !disabled },
    [onSearchOpen]
  );

  // ESC: Close modals/dialogs
  useHotkeys(
    'esc',
    () => {
      if (onModalClose) {
        onModalClose();
      }
    },
    { enabled: !disabled },
    [onModalClose]
  );

  // Cmd/Ctrl + /: Show keyboard shortcuts help
  useHotkeys(
    'mod+/',
    (e) => {
      e.preventDefault();
      // TODO: Open shortcuts help modal
      console.log('Keyboard shortcuts help');
    },
    { enabled: !disabled }
  );

  // Navigation shortcuts
  useHotkeys('g d', () => navigate('/dashboard'), { enabled: !disabled }, [navigate]);
  useHotkeys('g m', () => navigate('/members'), { enabled: !disabled }, [navigate]);
  useHotkeys('g g', () => navigate('/groups'), { enabled: !disabled }, [navigate]);
  useHotkeys('g u', () => navigate('/users'), { enabled: !disabled }, [navigate]);
  useHotkeys('g r', () => navigate('/reports'), { enabled: !disabled }, [navigate]);
  useHotkeys('g c', () => navigate('/claims'), { enabled: !disabled }, [navigate]);

  return {
    shortcuts: {
      search: 'Cmd/Ctrl + K',
      close: 'ESC',
      help: 'Cmd/Ctrl + /',
      dashboard: 'G then D',
      members: 'G then M',
      groups: 'G then G',
      users: 'G then U',
      reports: 'G then R',
      claims: 'G then C',
    },
  };
};

/**
 * Keyboard shortcuts for table/list views
 */
export const useTableShortcuts = (options: {
  onNew?: () => void;
  onRefresh?: () => void;
  disabled?: boolean;
} = {}) => {
  const { onNew, onRefresh, disabled = false } = options;

  // N: New item
  useHotkeys(
    'n',
    (e) => {
      e.preventDefault();
      if (onNew) onNew();
    },
    { enabled: !disabled },
    [onNew]
  );

  // R: Refresh
  useHotkeys(
    'r',
    (e) => {
      e.preventDefault();
      if (onRefresh) onRefresh();
    },
    { enabled: !disabled },
    [onRefresh]
  );

  return {
    shortcuts: {
      new: 'N',
      refresh: 'R',
    },
  };
};
